import styled from "@emotion/styled"
import { StandardClock } from "components/retailer-clock/CoverRetailClock"
import { useEffect, useMemo, useRef, useState } from "react"
import { useLocale } from "contexts/Locale"
import getMediaQuery from "css/breakpoints"
import { headline50, headline30, legend110, visuallyHidden } from "css/text"

const Root = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 1.875rem;
  justify-content: center;
  width: 100%;
  margin-block-start: -75px;
  /* padding-block: 4rem; */
`

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  text-align: center;
`

const Title = styled.p`
  ${headline30}
`

const Subtitle = styled.p`
  ${legend110}
`

const SimpleClock = styled(StandardClock)`
  margin: 0 !important;
  transform: translateY(0) !important;

  .needleColor {
    fill: rgb(var(--dark-green)) !important;
  }

  .fillColor {
    fill: rgb(var(--dark-green)) !important;
  }

  .fillStroke {
    stroke: rgb(var(--green)) !important;
  }
`

const CountdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5ex;
  margin-block-start: 2.5rem;
  place-items: center;
`

const TimeUnits = styled.div`
  --spacing: 0.5rem;
  ${getMediaQuery("m")} {
    --spacing: 1.25rem;
  }

  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  justify-content: center;
  width: max-content;

  > *:not(:last-of-type) {
    padding-inline: var(--spacing);
  }

  > *:not(:first-of-type) {
    padding-inline-start: var(--spacing);
    border-left: 1px solid rgb(var(--grey));
  }
`

const Num = styled.span`
  ${headline50}
`

const Label = styled.span`
  ${legend110}
`

const TimeUnitBox = styled.div`
  display: flex;
  flex-direction: column;
`

const ScreenReaderOnly = styled.div`
  cursor: default;
  opacity: 0;
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`

function getTimeRemaining(targetDate) {
  const target = Date.parse(targetDate)
  const now = Date.now()
  const diffRounded = Math.max(target - now + 999, 0)

  return {
    total: Math.max(target - now, 0),
    days: Math.floor(diffRounded / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diffRounded / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diffRounded / (1000 * 60)) % 60),
    seconds: Math.floor((diffRounded / 1000) % 60),
  }
}

const getFallbackLabels = locale => {
  const isFr = locale?.startsWith("fr")
  return {
    short: {
      day: { one: isFr ? "j" : "d", other: isFr ? "j" : "d" },
      hour: { one: "h", other: "h" },
      minute: { one: "m", other: "m" },
      second: { one: "s", other: "s" },
    },
    long: {
      day: { one: isFr ? "jour" : "day", other: isFr ? "jours" : "days" },
      hour: { one: isFr ? "heure" : "hour", other: isFr ? "heures" : "hours" },
      minute: { one: isFr ? "minute" : "minute", other: isFr ? "minutes" : "minutes" },
      second: { one: isFr ? "seconde" : "second", other: isFr ? "secondes" : "seconds" },
    },
  }
}

export default function Clock({ title, subtitle, start_date, event_time_zone, has_countdown, labels: rawLabels, sr_only }) {
  const locale = useLocale().current.codes.ws
  const timezone = event_time_zone ? { timeZoneId: event_time_zone } : undefined
  const [timeLeft, setTimeLeft] = useState(() => getTimeRemaining(start_date))
  const [ariaLiveKey, setAriaLiveKey] = useState(0)
  const timerRef = useRef()

  const labels = useMemo(() => {
    if (rawLabels?.[0]) return rawLabels[0]
    return getFallbackLabels(locale)
  }, [rawLabels, locale])

  useEffect(() => {
    if (!has_countdown || !start_date) return

    const tick = () => {
      const newTimeLeft = getTimeRemaining(start_date)
      setTimeLeft(prev => {
        if (prev.minutes !== newTimeLeft.minutes) {
          setAriaLiveKey(k => k + 1)
        }
        return newTimeLeft
      })

      if (newTimeLeft.total > 0) {
        const msToNextSecond = 1000 - (Date.now() % 1000)
        timerRef.current = setTimeout(tick, msToNextSecond)
      }
    }
    tick()

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [has_countdown, start_date])

  if (has_countdown && timeLeft.total > 0) {
    const renderUnits = () =>
      ["days", "hours", "minutes", "seconds"].map(unit => {
        const value = timeLeft[unit].toString().padStart(2, "0")
        const pluralForm = new Intl.PluralRules(locale).select(+value)
        return (
          <TimeUnitBox key={unit}>
            <Num>{value}</Num>
            <Label>{labels?.short?.[unit.slice(0, -1)]?.[pluralForm]}</Label>
          </TimeUnitBox>
        )
      })

    const renderUnitsA11y = () => {
      const units = ["days", "hours", "minutes"]
      const timeStrings = units
        .map(unit => {
          const value = timeLeft[unit]
          if (value === 0) return null
          const pluralForm = new Intl.PluralRules(locale).select(+value)
          const label = labels?.long?.[unit.slice(0, -1)]?.[pluralForm]
          return `${value} ${label}`
        })
        .filter(Boolean)

      const template = sr_only || (locale?.startsWith("fr") ? "Temps restant : %time%" : "Time remaining: %time%")
      return template.replace("%time%", timeStrings.join(", "))
    }

    return (
      <Root>
        <SimpleClock timezone={timezone} />
        <Heading>
          {title && <Title>{title}</Title>}
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </Heading>
        <CountdownContainer>
          <TimeUnits aria-hidden='true'>{renderUnits()}</TimeUnits>
          <ScreenReaderOnly key={ariaLiveKey} aria-live='polite' role='timer'>
            <p>{renderUnitsA11y()}</p>
          </ScreenReaderOnly>
        </CountdownContainer>
      </Root>
    )
  }

  return (
    <Root>
      <SimpleClock timezone={timezone} />
      <Heading>
        {title && <Title>{title}</Title>}
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </Heading>
    </Root>
  )
}
